/**
 * useInvitationsInbox — pending cross-product invitations for the Hub dashboard
 * (ADR-0010 M5c-3). Reads the enriched, email-bound `GET /v1/invitations/me` and
 * acts on invites in place via the token-less accept/decline path (the JWT
 * email-match is the capability — the list can't carry the hashed token).
 *
 * Uses the Cognito **ID token** (email-match needs the email claim — see
 * invitationApi.ts / DESIGN §Token).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@d-m-organization/hub-shared-lib/auth";
import {
  fetchMyInvitations,
  acceptInvitation,
  declineInvitation,
  type AcceptOutcome,
  type PendingInvitation,
} from "./invitationApi.js";

export interface InvitationsInbox {
  invites: PendingInvitation[];
  loading: boolean;
  busyId: string | null;
  reload: () => void;
  accept: (inv: PendingInvitation) => Promise<AcceptOutcome | null>;
  decline: (inv: PendingInvitation) => Promise<void>;
}

export function useInvitationsInbox(): InvitationsInbox {
  const { idToken, isAuthReady } = useAuth();
  const apiBase = (import.meta as ImportMeta).env.VITE_API_BASE_URL as string | undefined;

  const [invites, setInvites] = useState<PendingInvitation[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const idTokenRef = useRef<string | null>(null);
  idTokenRef.current = idToken;

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    if (!isAuthReady || !idToken || !apiBase) {
      setInvites([]);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    fetchMyInvitations({ apiBase, idToken, signal: controller.signal })
      .then((list) => {
        if (controller.signal.aborted) return;
        setInvites(list.filter((i) => i.status === "pending"));
        setLoading(false);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        // The inbox is a non-blocking surface — fail silently to an empty list.
        setInvites([]);
        setLoading(false);
      });
    return () => controller.abort();
  }, [apiBase, idToken, isAuthReady, reloadKey]);

  const remove = (id: string) => setInvites((cur) => cur.filter((i) => i.invitationId !== id));

  const accept = useCallback(async (inv: PendingInvitation): Promise<AcceptOutcome | null> => {
    const tok = idTokenRef.current;
    if (!tok || !apiBase) return null;
    setBusyId(inv.invitationId);
    try {
      // Token-less: the email-matched JWT is the capability (M5c-1).
      const out = await acceptInvitation({ apiBase, idToken: tok, invitationId: inv.invitationId });
      remove(inv.invitationId); // accepted or already-resolved → drop from the inbox
      return out;
    } catch {
      return null;
    } finally {
      setBusyId(null);
    }
  }, [apiBase]);

  const decline = useCallback(async (inv: PendingInvitation): Promise<void> => {
    const tok = idTokenRef.current;
    if (!tok || !apiBase) return;
    setBusyId(inv.invitationId);
    try {
      await declineInvitation({ apiBase, idToken: tok, invitationId: inv.invitationId });
      remove(inv.invitationId);
    } catch {
      /* leave the row; the user can retry */
    } finally {
      setBusyId(null);
    }
  }, [apiBase]);

  return { invites, loading, busyId, reload, accept, decline };
}
